/**
 * Écran de résultat DPE — direction "Apple Liquid Glass" (maquette retenue
 * dpe-sys-11). Présentationnel : reçoit un `ReportData` et l'affiche.
 *
 * Styles dans DpeResultScreen.css (scopés sous `.dpe-lg`). Police Mona Sans
 * chargée dans index.html. La mention « sans valeur légale » (F-RAPP-003)
 * reste présente (pill + bandeau bas).
 */
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import type { ReportData } from "@/api/dpe";
import "./DpeResultScreen.css";

const ORDER = ["A", "B", "C", "D", "E", "F", "G"] as const;
const SPECTRUM: Record<string, string> = {
  A: "#34c759", B: "#86d94a", C: "#cdd836", D: "#ffd60a", E: "#ff9f0a", F: "#ff6a2c", G: "#ff3b30",
};
const classColor = (l?: string) => SPECTRUM[(l ?? "").toUpperCase()] ?? "#8e94a4";

function parseFrNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const cleaned = v.replace(/[\s ]/g, "").replace(/[^0-9,.-]/g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}
const fmtInt = (n: number) => Math.round(n).toLocaleString("fr-FR");

/** Phrase de synthèse selon la classe (copie produit légère, non chiffrée). */
function verdict(label: string): string {
  const L = (label ?? "").toUpperCase();
  if (["F", "G"].includes(L))
    return "Votre logement consomme beaucoup plus que la moyenne. Une rénovation aurait un fort impact sur vos factures.";
  if (["D", "E"].includes(L))
    return "Votre logement consomme plus que la moyenne de sa génération. Des travaux ciblés le feraient progresser.";
  if (["A", "B"].includes(L))
    return "Votre logement est très performant. Quelques gestes simples peuvent encore l'améliorer.";
  return "Votre logement est correctement isolé. Des travaux ciblés peuvent encore l'optimiser.";
}

type Scenario = ReportData["scenarios_detail"][number];
const RELEVANT = new Set(["excellent", "bon", "ok", "marginal"]);
/** Choisit le scénario « vitrine » : le plus économe parmi les pertinents. */
function bestScenario(list: Scenario[]): Scenario | null {
  const ok = list.filter((s) => RELEVANT.has(s.relevance));
  if (ok.length === 0) return null;
  return ok.reduce((best, s) =>
    (parseFrNum(s.annual_eur_saved) ?? 0) > (parseFrNum(best.annual_eur_saved) ?? 0) ? s : best,
  );
}

export function DpeResultScreen({
  data,
  logementId,
  embedded = false,
  onRefineScenarios,
}: {
  data: ReportData;
  logementId: string;
  /** Intégré dans un onglet (pas en plein écran) : masque la barre du haut
   *  (Retour + marque) et confine le fond au conteneur. */
  embedded?: boolean;
  /** Action du CTA « Affiner mes scénarios ». Si fourni (mode intégré), le CTA
   *  appelle ce callback au lieu de naviguer vers la page de saisie. */
  onRefineScenarios?: () => void;
}) {
  const energyColor = classColor(data.energy_label);
  const gesColor = classColor(data.ges_label);

  // --- Coût annuel : préférer l'objet V4 numérique, sinon les chaînes ---
  const ac = data.annual_cost;
  const costCentral = ac
    ? `${fmtInt(ac.central)} €`
    : parseFrNum(data.annual_costs_central) != null
      ? `${fmtInt(parseFrNum(data.annual_costs_central)!)} €`
      : null;
  const costMin = ac ? `${fmtInt(ac.min)} €` : data.annual_costs_total_min;
  const costMax = ac ? `${fmtInt(ac.max)} €` : data.annual_costs_total_max;

  // --- Profils d'usage (sobre / standard / intensif) ---
  // La facture dépend fortement du comportement (chauffage surtout). On encadre
  // donc le coût conventionnel ("standard") par un usage sobre et un usage
  // intensif, plutôt que d'afficher un chiffre unique trompeur.
  const usageOrder: { key: string; label: string; hint: string }[] = [
    { key: "sobre", label: "Sobre", hint: "chauffage modéré, pièces fraîches" },
    { key: "standard", label: "Standard", hint: "usage conventionnel (DPE)" },
    { key: "intensif", label: "Intensif", hint: "confort élevé, présence forte" },
  ];
  const usageRows = data.usage_scenarios
    ? usageOrder
        .map((p) => ({ ...p, v: data.usage_scenarios![p.key] }))
        .filter((p) => p.v)
    : [];

  // --- Bâti ancien (avant 1948) ---
  const bati = data.bati_ancien?.is_ancien ? data.bati_ancien : null;
  const adviceIcon = (t: string) => (t === "avoid" ? "⚠️" : t === "do" ? "✓" : "ℹ️");

  // --- CO₂ : kg → tonnes si pertinent ---
  const co2kg = parseFrNum(data.co2_total_kg_per_year);
  const co2Display =
    co2kg == null
      ? `${data.co2_total_kg_per_year}`
      : co2kg >= 1000
        ? `${(co2kg / 1000).toFixed(1).replace(".", ",")} t`
        : `${fmtInt(co2kg)} kg`;

  // --- Confort d'été ---
  // Verdict = classe officielle (Insuffisant/Moyen/Bon) ; les points et le
  // "/100" reflètent l'indice continu indicatif (score_continu), avec repli
  // sur le score 3-valeurs si l'indice n'est pas disponible.
  const sc = data.summer_comfort;
  const comfortLabel = sc?.classe ? sc.classe.charAt(0).toUpperCase() + sc.classe.slice(1) : "—";
  const comfortScore = sc?.score_continu ?? sc?.score ?? null;
  const comfortDots = comfortScore != null ? Math.max(1, Math.min(5, Math.round(comfortScore / 20))) : 0;

  // --- Déperditions ---
  const losses = [...(data.losses_pct ?? [])].sort((a, b) => b.pct - a.pct);
  const maxPct = Math.max(1, ...losses.map((l) => l.pct));

  // --- Projection ---
  const scen = bestScenario(data.scenarios_detail ?? []);
  const beforeKwh = scen ? parseFrNum(scen.primary_per_m2_before) : null;
  const afterKwh = scen ? parseFrNum(scen.new_consumption_per_m2) : null;
  const reductionPct =
    beforeKwh && afterKwh && beforeKwh > 0
      ? Math.round(((beforeKwh - afterKwh) / beforeKwh) * 100)
      : null;
  const saved = scen ? parseFrNum(scen.annual_eur_saved) : null;

  const chips = [
    data.shab_m2 ? `${data.shab_m2} m²` : null,
    data.construction_year ? `${data.construction_year}` : null,
    data.climate_zone ? `Zone ${data.climate_zone}` : null,
    data.equipment?.heating_label || null,
  ].filter(Boolean) as string[];

  return (
    <div className={embedded ? "dpe-lg embedded" : "dpe-lg"}>
      <div className="bg" aria-hidden="true" />
      <div className="wrap">
        {/* ---------- TOP BAR (masquée en mode intégré) ---------- */}
        {!embedded && (
          <header className="top reveal">
            <div className="left">
              <Link className="back" to={`/logements/${logementId}`}>
                <svg viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Retour
              </Link>
              <div className="brand">
                <span className="m" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M12 21c5-2 8-6 8-11V5l-8-2-8 2v5c0 5 3 9 8 11Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" /></svg></span>
                <span><b>Mon Diag Énergétique</b><span>Estimation énergétique</span></span>
              </div>
            </div>
            <span className="pill"><span className="d" /> Sans valeur légale</span>
          </header>
        )}

        {/* ---------- HERO ---------- */}
        <section className="hero glass reveal" style={{ animationDelay: ".05s" }}>
          <div>
            <span className="eyebrow">Diagnostic de performance énergétique</span>
            <h1>{data.logement_title}</h1>
            <div className="chips">
              {chips.map((c, i) => <span className="chip" key={i}>{c}</span>)}
            </div>
            <p className="verdict">{verdict(data.energy_label)}</p>
            <div>
              <span className="ges-pill">
                GES <b>{data.ges_kgco2_per_m2} kg CO₂/m²·an</b>
                <span className="bb" style={{ background: gesColor }}>{data.ges_label}</span>
              </span>
            </div>
          </div>
          <div className="dial">
            <div className="cbadge" style={{ background: energyColor, "--badge": energyColor } as CSSProperties}>
              <span className="L">{data.energy_label}</span>
              <span className="n">{data.energy_kwh_per_m2} kWh ep/m²·an</span>
            </div>
            <div className="softscale" role="img" aria-label={`Classe énergie ${data.energy_label}`}>
              {ORDER.map((c) => (
                <i key={c} className={c === (data.energy_label ?? "").toUpperCase() ? "on" : ""}
                   style={{ background: SPECTRUM[c], color: SPECTRUM[c] }} />
              ))}
            </div>
          </div>
        </section>

        {/* ---------- WIDGETS ---------- */}
        <section className="stats">
          <div className="stat glass reveal" style={{ animationDelay: ".1s" }}>
            <span className="ic"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3v18M8.5 7c0-1.6 1.6-2.8 3.5-2.8S15.5 5.4 15.5 7c0 3.2-7 1.9-7 5.4 0 1.7 1.6 2.9 3.5 2.9s3.5-1.2 3.5-2.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></span>
            <div className="k">Coût annuel estimé</div>
            <div className="v">{costCentral ?? `${costMin} – ${costMax}`}</div>
            <div className="s">{costCentral ? <>fourchette <b>{costMin} – {costMax}</b></> : "abonnement compris"}</div>
          </div>
          <div className="stat glass reveal" style={{ animationDelay: ".14s" }}>
            <span className="ic b"><svg viewBox="0 0 24 24" fill="none"><path d="M5 16c8 1 11-4 13-12-8 1-13 4-13 12Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg></span>
            <div className="k">Empreinte carbone</div>
            <div className="v">{co2Display} <b>CO₂</b></div>
            <div className="s">par an{data.co2_equiv_km && data.co2_equiv_km !== "—" ? <> · ≈ <b>{data.co2_equiv_km} km</b> en voiture</> : null}</div>
          </div>
          <div className="stat glass reveal" style={{ animationDelay: ".18s" }}>
            <span className="ic c"><svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.7" /><path d="M12 3v2M12 19v2M5 5l1.4 1.4M17.6 17.6 19 19M3 12h2M19 12h2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></span>
            <div className="k">Confort d'été</div>
            <div className="v">{comfortLabel}</div>
            {comfortDots > 0 && (
              <div className="dots" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => <i key={i} className={i < comfortDots ? "on" : ""} />)}
              </div>
            )}
            {comfortScore != null && <div className="s">indice {comfortScore}/100</div>}
          </div>
        </section>

        {/* ---------- BÂTI ANCIEN (avant 1948) ---------- */}
        {bati && (
          <section className="bati glass reveal">
            <div className="bati-head">
              <span className="bati-ic" aria-hidden="true">🏛️</span>
              <div>
                <h3>{bati.title}</h3>
                <p>{bati.summary}</p>
                <p className="bati-disclaimer" style={{ fontSize: "0.8rem", color: "var(--ink-2)", marginTop: 8 }}>
                  Ces conseils sont informatifs et ne remplacent pas l'avis d'un professionnel qualifié.
                </p>
              </div>
            </div>
            <ul className="bati-advice">
              {bati.advice.map((a, i) => (
                <li key={i} className={`adv-${a.type}`}>
                  <span className="adv-ic" aria-hidden="true">{adviceIcon(a.type)}</span>
                  <span>{a.text}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ---------- PROFILS D'USAGE ---------- */}
        {usageRows.length > 0 && (
          <>
            <div className="sh">
              <h2>Votre facture selon votre usage</h2>
              <span className="hint">le comportement compte autant que le bâti</span>
            </div>
            <section className="usage glass reveal">
              {usageRows.map((p) => {
                const isStd = p.key === "standard";
                return (
                  <div className={`urow${isStd ? " std" : ""}`} key={p.key}>
                    <div className="ulab">
                      <b>{p.label}</b>
                      {isStd && <span className="tag">référence DPE</span>}
                      <small>{p.hint}</small>
                    </div>
                    <div className="uval">
                      <span className="c">{fmtInt(p.v.cost_central)} €</span>
                      <small>{fmtInt(p.v.cost_min)} – {fmtInt(p.v.cost_max)} € / an</small>
                    </div>
                  </div>
                );
              })}
              <p className="unote">
                Estimation indicative. Le coût « Standard » correspond au calcul
                conventionnel du DPE ; un usage plus sobre ou plus intensif fait
                surtout varier le chauffage. Abonnements compris.
              </p>
            </section>
          </>
        )}

        {/* ---------- DÉPERDITIONS ---------- */}
        {losses.length > 0 && (
          <>
            <div className="sh"><h2>Par où s'échappe la chaleur</h2><span className="hint">vos meilleurs candidats travaux</span></div>
            <section className="losses glass reveal">
              {losses.map((l) => (
                <div className="loss" key={l.key}>
                  <span className="lab">{l.label}</span>
                  <div className="track"><div className="fill" style={{ width: `${(l.pct / maxPct) * 100}%` }} /></div>
                  <span className="v">{Math.round(l.pct)} %<small>{l.value_w_per_k} W/K</small></span>
                </div>
              ))}
            </section>
          </>
        )}

        {/* ---------- PROJECTION ---------- */}
        <div className="sh"><h2>Et si vous rénoviez ?</h2><span className="hint">projection avec un bouquet adapté</span></div>
        <section className="after glass reveal">
          <span className="eyebrow">Projection après rénovation</span>
          {scen ? (
            <>
              <h2>
                De {scen.label_before} à {scen.new_energy_label}
                {reductionPct != null ? ` · −${reductionPct} %` : ""}.
              </h2>
              <div className="after-grid">
                <div className="jump">
                  <div>
                    <div className="sq" style={{ background: classColor(scen.label_before) }}>{scen.label_before}</div>
                    <div className="cap">Aujourd'hui{beforeKwh != null ? <><br />{fmtInt(beforeKwh)} kWh</> : null}</div>
                  </div>
                  <div className="ar"><svg viewBox="0 0 26 18" width="26" height="18" fill="none"><path d="M2 9h20m0 0-6-6m6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                  <div>
                    <div className="sq" style={{ background: classColor(scen.new_energy_label) }}>{scen.new_energy_label}</div>
                    <div className="cap">Après travaux{afterKwh != null ? <><br />{fmtInt(afterKwh)} kWh</> : null}</div>
                  </div>
                </div>
                <div>
                  <div className="kpis">
                    <div className="kpi"><div className="k">Économie / an</div><div className="v g">{saved && saved > 0 ? `~${fmtInt(saved)} €` : "—"}</div></div>
                    <div className="kpi"><div className="k">Aides</div><div className="v">{scen.aids_total && scen.aids_total !== "—" ? scen.aids_total : "—"}</div></div>
                    <div className="kpi"><div className="k">Retour</div><div className="v">{scen.roi_years && scen.roi_years !== "—" ? `~${scen.roi_years} ans` : "—"}</div></div>
                  </div>
                  {scen.improvements.length > 0 && (
                    <div className="works">{scen.improvements.map((w, i) => <span key={i}>{w}</span>)}</div>
                  )}
                  <div className="cta-row">
                    {onRefineScenarios ? (
                      <button type="button" className="cta" onClick={onRefineScenarios}>
                        <svg viewBox="0 0 24 24" fill="none"><path d="M12 21c5-2 8-6 8-11V5l-8-2-8 2v5c0 5 3 9 8 11Z" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" /></svg>
                        Affiner mes scénarios
                      </button>
                    ) : (
                      <Link className="cta" to={`/logements/${logementId}/dpe`}>
                        <svg viewBox="0 0 24 24" fill="none"><path d="M12 21c5-2 8-6 8-11V5l-8-2-8 2v5c0 5 3 9 8 11Z" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" /></svg>
                        Affiner mes scénarios
                      </Link>
                    )}
                    <span className="cta-note">Conseil gratuit France Rénov' · 0 808 800 700</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2>Estimez le potentiel de votre logement.</h2>
              <p className="sub">Simulez un bouquet de travaux (isolation, pompe à chaleur, vitrage…) pour voir la nouvelle étiquette, les économies attendues et les aides MaPrimeRénov' mobilisables.</p>
              <div className="cta-row">
                <Link className="cta" to={`/logements/${logementId}/dpe`}>
                  <svg viewBox="0 0 24 24" fill="none"><path d="M12 21c5-2 8-6 8-11V5l-8-2-8 2v5c0 5 3 9 8 11Z" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" /></svg>
                  Simuler des travaux
                </Link>
                <span className="cta-note">Conseil gratuit France Rénov' · 0 808 800 700</span>
              </div>
            </>
          )}
        </section>

        {/* ---------- LÉGAL ---------- */}
        <footer className="legal glass reveal">
          <span className="s" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" /><path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg></span>
          <p>
            <b>{data.disclaimer_title || "Estimation pédagogique — sans valeur légale."}</b>{" "}
            {data.disclaimer_body ||
              "Ce document n'est pas un DPE réglementaire et ne comporte aucun numéro ADEME. Coûts, aides et gains indicatifs. Pour un DPE officiel, faites appel à un diagnostiqueur certifié."}
            <span className="e">Moteur 3CL‑DPE {data.engine_version} · réf. {data.internal_reference}</span>
          </p>
        </footer>
      </div>
    </div>
  );
}
